/** The "of N" half of the grid pager, for grids that don't always know N.
 *
 *  Feeds `slotProps.pagination.labelDisplayedRows`, which MUI calls with
 *  `{ from, to, count, estimated }` and accepts a node back. Four renderings:
 *
 *  - exact       `1–25 of 12,340`                    (sync, or a resolved total)
 *  - pending     `1–25 of` + spinner                 (a count is being computed)
 *  - unknown     `1–25 of` + **Show total**          (deferred, awaiting a click)
 *  - unavailable `1–25 of —` + retry                 (the count was too slow)
 *
 *  `estimatedRowCount` is deliberately unused: every one of these grids is
 *  filtered, so a cheap estimate would be wrong, and "more than 25" is more
 *  honest than a confident "≈ 4,300,000".
 */
import { Box, CircularProgress, Link, Tooltip, Typography } from '@mui/material';

import type { CountDisplayState } from './useCountMode';

export interface CountTotalLabelProps {
  from: number;
  to: number;
  state: CountDisplayState;
  total: number | null;
  /** Whether an on-demand total can be requested at all. */
  canRequest: boolean;
  onRequest: () => void;
}

export function CountTotalLabel({
  from,
  to,
  state,
  total,
  canRequest,
  onRequest,
}: CountTotalLabelProps) {
  const range = `${from.toLocaleString()}–${to.toLocaleString()}`;

  if (state === 'exact' && total !== null) {
    return <>{`${range} of ${total.toLocaleString()}`}</>;
  }

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <span>{`${range} of`}</span>
      {state === 'pending' && (
        <Tooltip title="Calculating the total…">
          <CircularProgress size={12} thickness={5} aria-label="Calculating the total" />
        </Tooltip>
      )}
      {state === 'unknown' &&
        (canRequest ? (
          <Tooltip title="Count all matching records">
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={onRequest}
              sx={{ font: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Show total
            </Link>
          </Tooltip>
        ) : (
          <Typography component="span" variant="inherit" color="text.disabled">
            many
          </Typography>
        ))}
      {state === 'unavailable' && (
        <Tooltip title="The total could not be calculated — this search is too broad to count quickly.">
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={onRequest}
            sx={{ font: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}
            color="text.secondary"
          >
            unavailable — retry
          </Link>
        </Tooltip>
      )}
    </Box>
  );
}

/** Build the `labelDisplayedRows` callback MUI's pagination slot expects. */
export function countLabelDisplayedRows(args: {
  state: CountDisplayState;
  total: number | null;
  canRequest: boolean;
  onRequest: () => void;
}) {
  // Named so React DevTools (and eslint) see a component, not an anonymous
  // arrow — MUI calls this as a render function.
  const DisplayedRows = ({ from, to }: { from: number; to: number }) => (
    <CountTotalLabel from={from} to={to} {...args} />
  );
  return DisplayedRows;
}
